/* eslint-disable no-restricted-globals */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Card } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts';
import { selectChartLogs, selectModelUsage } from '../../selectors';
import { getInputTokens, getPlainInputTokens, getTotalTokens } from '../../utils';

const UsageByModelChart = () => {
  const usage = useSelector(selectModelUsage);
  const chartLogs = useSelector(selectChartLogs);
  const models = usage?.models ?? [];

  // Fallback for logs without cost: estimate from each model's cost per token over the period
  const costPerToken = models.reduce((acc, m) => {
    const tokens = Number(m.tokenUsage?.totalTokenCount) || 0;
    acc[m.model] = tokens > 0 ? (Number(m.cost?.totalCost) || 0) / tokens : 0;
    return acc;
  }, {});

  /**
   * Processes raw LLM logs into aggregated daily datasets for MUI Charts.
   *
   * @param {Array} logs - Array of log objects
   * @param {Object} options - Configuration options
   * @param {boolean} options.fillMissingDates - Fill dates with 0 usage if true (default: false)
   * @returns {{ dailyTokenDataset: Array, dailyModelDataset: Array, uniqueModels: Array }}
   */
  const processLLMLogs = (options = {}) => {
    const { fillMissingDates = false } = options;

    if (!Array.isArray(chartLogs) || chartLogs.length === 0) {
      return { dailyTokenDataset: [], dailyModelDataset: [], uniqueModels: [] };
    }

    const tokenMap = {};
    const modelMap = {};
    const modelsSet = new Set();

    chartLogs.forEach((log) => {
      if (!log || !log.createdAt) return;
      const createdAt = new Date(log.createdAt);
      if (isNaN(createdAt.getTime())) return;

      // 1. Extract YYYY-MM-DD from timestamp
      const date = createdAt.toISOString().split('T')[0];
      const model = log.model || 'Unknown';
      modelsSet.add(model);

      // Extract token counts safely
      const tokenUsage = log.tokenUsage || log; // Handles nested tokenUsage or flat token properties
      const input = getInputTokens(tokenUsage);
      const output = Number(tokenUsage.outputTokenCount) || 0;
      const cached = Number(tokenUsage.cachedTokenCount) || 0;
      const reasoning = Number(tokenUsage.reasoningTokenCount) || 0;
      const total = getTotalTokens(tokenUsage);
      const logCost =
        log.cost?.totalCost != null
          ? Number(log.cost.totalCost) || 0
          : total * (costPerToken[model] ?? 0);

      // 2. Aggregate dailyTokenDataset (Token Types Breakdown)
      if (!tokenMap[date]) {
        tokenMap[date] = {
          date,
          inputTokenCount: 0,
          outputTokenCount: 0,
          cachedTokenCount: 0,
          reasoningTokenCount: 0,
          totalTokenCount: 0,
          totalCost: 0,
        };
      }
      tokenMap[date].inputTokenCount += input;
      tokenMap[date].outputTokenCount += output;
      tokenMap[date].cachedTokenCount += cached;
      tokenMap[date].reasoningTokenCount += reasoning;
      tokenMap[date].totalTokenCount += total;
      tokenMap[date].totalCost += logCost;

      // 3. Aggregate dailyModelDataset (Per-Model Volume)
      if (!modelMap[date]) {
        modelMap[date] = { date };
      }
      modelMap[date][model] = (modelMap[date][model] || 0) + total;
    });

    // Sort dates chronologically
    const sortedDates = Object.keys(tokenMap).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    let dailyTokenDataset = sortedDates.map((date) => tokenMap[date]);
    let dailyModelDataset = sortedDates.map((date) => modelMap[date]);

    // Optional: Fill missing calendar days between start and end dates with zeroes
    if (fillMissingDates && sortedDates.length > 1) {
      const filledTokenDataset = [];
      const filledModelDataset = [];

      const startDate = new Date(sortedDates[0]);
      const endDate = new Date(sortedDates[sortedDates.length - 1]);

      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];

        filledTokenDataset.push(
          tokenMap[dateStr] || {
            date: dateStr,
            inputTokenCount: 0,
            outputTokenCount: 0,
            cachedTokenCount: 0,
            reasoningTokenCount: 0,
            totalTokenCount: 0,
            totalCost: 0,
          }
        );

        filledModelDataset.push(modelMap[dateStr] || { date: dateStr });
      }

      dailyTokenDataset = filledTokenDataset;
      dailyModelDataset = filledModelDataset;
    }

    // Ensure all model keys exist in every object (prevents `undefined` in LineChart)
    const uniqueModels = Array.from(modelsSet);
    dailyModelDataset = dailyModelDataset.map((entry) => {
      const formattedEntry = { date: entry.date };
      uniqueModels.forEach((model) => {
        formattedEntry[model] = entry[model] || 0;
      });
      return formattedEntry;
    });

    return {
      dailyTokenDataset,
      dailyModelDataset,
      uniqueModels,
    };
  };

  const { dailyModelDataset, dailyTokenDataset, uniqueModels } = processLLMLogs();
  const modelColors = ['#3b82f6', '#10b981', '#c084fc', '#f59e0b', '#ef4444'];

  const dataset = models.map((m) => ({
    model: m.model,
    plainInputTokenCount: getPlainInputTokens(m.tokenUsage),
    cachedTokenCount: m.tokenUsage?.cachedTokenCount ?? 0,
    cacheWriteTokenCount: m.tokenUsage?.cacheWriteTokenCount ?? 0,
    outputTokenCount: m.tokenUsage?.outputTokenCount ?? 0,
  }));

  return (
    <>
      <Card
        elevation={0}
        sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, backgroundColor: '#ffffff' }}
      >
        {/* Header section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
            Usage by model
          </Typography>
        </Box>

        {/* Chart container */}
        <Box sx={{ width: '100%', height: 320 }} flexDirection="column">
          {models.length === 0 ? (
            <Typography variant="body2">No usage data for the selected period</Typography>
          ) : (
            <BarChart
              dataset={dataset}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey: 'model',
                  categoryGapRatio: 0.3,
                },
              ]}
              yAxis={[
                {
                  valueFormatter: (value) => value.toLocaleString(),
                },
              ]}
              series={[
                {
                  dataKey: 'plainInputTokenCount',
                  label: 'Input tokens (uncached)',
                  stack: 'total',
                  color: '#3b82f6',
                },
                {
                  dataKey: 'cachedTokenCount',
                  label: 'Cached tokens',
                  stack: 'total',
                  color: '#c084fc',
                },
                {
                  dataKey: 'cacheWriteTokenCount',
                  label: 'Cache write tokens',
                  stack: 'total',
                  color: '#f59e0b',
                },
                {
                  dataKey: 'outputTokenCount',
                  label: 'Output tokens',
                  stack: 'total',
                  color: '#10b981',
                },
              ]}
              grid={{ horizontal: true }}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'left' },
                  padding: 0,
                  itemGap: 16,
                  labelStyle: {
                    fontSize: 12,
                    fill: '#475569',
                  },
                },
              }}
              margin={{ top: 20, bottom: 50, left: 40, right: 10 }}
            />
          )}
        </Box>
      </Card>
      <Card
        elevation={0}
        sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, backgroundColor: '#ffffff' }}
      >
        {/* Header section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
            Daily Cost
          </Typography>
        </Box>

        {/* Chart container */}
        <Box sx={{ width: '100%', height: 320 }}>
          {models.length === 0 ? (
            <Typography variant="body2">No usage data for the selected period</Typography>
          ) : (
            <LineChart
              dataset={dailyTokenDataset ?? []}
              xAxis={[
                {
                  dataKey: 'date',
                  scaleType: 'point',
                  valueFormatter: (date) => {
                    if (!date) return '';
                    const parsed = new Date(date);
                    return isNaN(parsed.getTime())
                      ? String(date)
                      : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  },
                },
              ]}
              yAxis={[
                {
                  id: 'costAxis',
                  valueFormatter: (value) =>
                    value != null && !isNaN(value) ? `$${Number(value).toFixed(2)}` : '',
                },
                {
                  id: 'tokenAxis',
                  valueFormatter: (value) =>
                    value != null && !isNaN(value) ? Number(value).toLocaleString() : '',
                },
              ]}
              series={[
                {
                  dataKey: 'totalCost',
                  label: 'Daily Cost ($)',
                  yAxisId: 'costAxis',
                  color: '#ef4444',
                  curve: 'smooth',
                },
                {
                  dataKey: 'totalTokenCount',
                  label: 'Total Tokens',
                  yAxisId: 'tokenAxis',
                  color: '#3b82f6',
                  curve: 'smooth',
                },
              ]}
              grid={{ horizontal: true }}
              margin={{ top: 20, bottom: 50, left: 60, right: 60 }}
              height={350}
            />
          )}
        </Box>
      </Card>
      <Card
        elevation={0}
        sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, backgroundColor: '#ffffff' }}
      >
        {/* Header section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
            Usage by model
          </Typography>
        </Box>

        {/* Chart container */}
        <Box sx={{ width: '100%', height: 320 }}>
          {models.length === 0 ? (
            <Typography variant="body2">No usage data for the selected period</Typography>
          ) : (
            <LineChart
              dataset={dailyModelDataset}
              xAxis={[{ scaleType: 'point', dataKey: 'date' }]}
              yAxis={[{ valueFormatter: (val) => val.toLocaleString() }]}
              series={uniqueModels.map((model, index) => ({
                dataKey: model,
                label: model,
                curve: 'smooth',
                color: modelColors[index % modelColors.length],
              }))}
              grid={{ horizontal: true }}
              height={350}
            />
          )}
        </Box>
      </Card>
    </>
  );
};

export default UsageByModelChart;
